import { createContext, useContext, useState, useRef, useCallback, useEffect, ReactNode } from 'react';

// ────────────────────────────────────────────
// Types
// ────────────────────────────────────────────

export interface AudioTrack {
    id: string;
    url: string;
    title: string;
    authorName: string;
    authorIcon?: string | null;
    imageUrl?: string | null;
    postId?: string;
}

interface AudioContextValue {
    currentTrack: AudioTrack | null;
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    play: (track: AudioTrack) => void;
    pause: () => void;
    resume: () => void;
    toggle: () => void;
    seek: (time: number) => void;
    stop: () => void;
    audioElement: HTMLAudioElement | null;
}

const AudioContext = createContext<AudioContextValue | null>(null);

// ────────────────────────────────────────────
// Hook
// ────────────────────────────────────────────

export function useAudio(): AudioContextValue {
    const ctx = useContext(AudioContext);
    if (!ctx) throw new Error('useAudio must be used within <AudioProvider>');
    return ctx;
}

// ────────────────────────────────────────────
// Provider
// ────────────────────────────────────────────

export function AudioProvider({ children }: { children: ReactNode }) {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [currentTrack, setCurrentTrack] = useState<AudioTrack | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    // Lazily create a single audio element that persists for the lifetime of the app
    useEffect(() => {
        const audio = new Audio();
        audio.preload = 'metadata';
        audioRef.current = audio;

        const onTimeUpdate = () => setCurrentTime(audio.currentTime);
        const onDurationChange = () => setDuration(audio.duration || 0);
        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);
        const onEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
        };

        audio.addEventListener('timeupdate', onTimeUpdate);
        audio.addEventListener('durationchange', onDurationChange);
        audio.addEventListener('play', onPlay);
        audio.addEventListener('pause', onPause);
        audio.addEventListener('ended', onEnded);

        return () => {
            audio.removeEventListener('timeupdate', onTimeUpdate);
            audio.removeEventListener('durationchange', onDurationChange);
            audio.removeEventListener('play', onPlay);
            audio.removeEventListener('pause', onPause);
            audio.removeEventListener('ended', onEnded);
            audio.pause();
            audio.src = '';
        };
    }, []);

    const play = useCallback((track: AudioTrack) => {
        const audio = audioRef.current;
        if (!audio) return;

        // If same track, just resume
        if (currentTrack?.url === track.url) {
            audio.play().catch(() => {});
            return;
        }

        // New track
        audio.pause();
        audio.src = track.url;
        audio.currentTime = 0;
        setCurrentTrack(track);
        setCurrentTime(0);
        setDuration(0);
        audio.play().catch(() => {});
    }, [currentTrack]);

    const pause = useCallback(() => {
        audioRef.current?.pause();
    }, []);

    const resume = useCallback(() => {
        audioRef.current?.play().catch(() => {});
    }, []);

    const toggle = useCallback(() => {
        if (isPlaying) {
            pause();
        } else {
            resume();
        }
    }, [isPlaying, pause, resume]);

    const seek = useCallback((time: number) => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.currentTime = time;
        setCurrentTime(time);
    }, []);

    const stop = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.pause();
        audio.src = '';
        setCurrentTrack(null);
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
    }, []);

    return (
        <AudioContext.Provider value={{ currentTrack, isPlaying, currentTime, duration, play, pause, resume, toggle, seek, stop, audioElement: audioRef.current }}>
            {children}
        </AudioContext.Provider>
    );
}
